import { dictionaries } from "@/dictionaries/content";
import type { IBoundarySchoolLink } from "@/shared/types/boundary-school-link.types";
import {
  boundarySchoolLinksFromFeatureProperties,
  primaryBoundarySchoolIdentity,
} from "@/shared/utils/boundary-school-links.util";
import { resolveBoundarySchoolsDisplayName } from "@/shared/utils/resolve-boundary-schools-display-name";

export interface UnassignedBoundaryIdentity {
  schoolName?: string | null;
  schoolInternalId?: string | null;
  schoolId?: string | null;
}

export function getUnassignedBoundaryLabel(): string {
  return dictionaries.mapPage?.boundaryPanel?.unassignedBoundary ?? "Unassigned Boundary";
}

export function isUnassignedBoundary(params: UnassignedBoundaryIdentity): boolean {
  if (params.schoolName?.trim()) {
    return false;
  }

  if (params.schoolInternalId?.trim()) {
    return false;
  }

  if (params.schoolId?.trim()) {
    return false;
  }

  return true;
}

export function resolveBoundaryDisplayName(
  schoolName: string | null | undefined,
  unassignedLabel: string = getUnassignedBoundaryLabel(),
): string {
  const trimmed = schoolName?.trim();
  if (!trimmed) {
    return unassignedLabel;
  }

  return trimmed;
}

export function resolveZoneListItemLabel(params: {
  schoolName?: string | null;
  schoolInternalId?: string | null;
  schoolId?: string | null;
  fallbackId?: string | null;
}): string {
  const trimmedName = params.schoolName?.trim();
  if (trimmedName) {
    return trimmedName;
  }

  const trimmedInternalId = params.schoolInternalId?.trim();
  if (trimmedInternalId) {
    return trimmedInternalId;
  }

  if (isUnassignedBoundary(params)) {
    return getUnassignedBoundaryLabel();
  }

  const fallback = params.fallbackId?.trim();
  if (fallback) {
    return fallback;
  }

  return getUnassignedBoundaryLabel();
}

/** Zone/list label from full `schools[]` (slash-merge), with existing unassigned fallbacks. */
export function resolveZoneListItemLabelFromSchools(
  schools: readonly IBoundarySchoolLink[] | null | undefined,
  fallbackId?: string | null,
): string {
  const identity = primaryBoundarySchoolIdentity(schools);
  const mergedName = resolveBoundarySchoolsDisplayName(schools, { unassignedLabel: "" });

  return resolveZoneListItemLabel({
    schoolName: mergedName || null,
    schoolInternalId: identity.schoolInternalId || null,
    schoolId: identity.schoolId,
    fallbackId,
  });
}

export function resolveZoneListItemLabelFromFeatureProperties(
  properties: Record<string, unknown>,
  fallbackId?: string | null,
): string {
  return resolveZoneListItemLabelFromSchools(
    boundarySchoolLinksFromFeatureProperties(properties),
    fallbackId,
  );
}

export function resolveBoundaryShapeDisplayValue(params: {
  schoolName?: string | null;
  schoolInternalId?: string | null;
  schoolId?: string | null;
  fallbackValue: string;
}): string {
  if (params.schoolName?.trim()) {
    return params.schoolName.trim();
  }

  if (isUnassignedBoundary(params)) {
    return getUnassignedBoundaryLabel();
  }

  return params.fallbackValue;
}

/** Shape card value from full `schools[]` (slash-merge). */
export function resolveBoundaryShapeDisplayValueFromSchools(
  schools: readonly IBoundarySchoolLink[] | null | undefined,
  fallbackValue: string,
): string {
  const identity = primaryBoundarySchoolIdentity(schools);
  const mergedName = resolveBoundarySchoolsDisplayName(schools, { unassignedLabel: "" });

  return resolveBoundaryShapeDisplayValue({
    schoolName: mergedName || null,
    schoolInternalId: identity.schoolInternalId || null,
    schoolId: identity.schoolId,
    fallbackValue,
  });
}
